import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import axios from 'axios';
import { config } from '../../../config';
import { v4 as uuidv4 } from 'uuid';
import './google-auto-suggest.css';
import locationIcon from '../../../assets/icons/location.svg';

const AutocompleteWithDebounce = forwardRef((props, ref) => {
  const {onSelectAddress, className, name, placeHolder, inputType, inputVal} = props;
  const [inputValue, setInputValue] = useState('');
  const searchOptions = inputType === 'city' ? ['locality', 'sublocality'] : ['geocode', 'establishment'];
  const [sessionToken, setSessionToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [placePredictions, setPlacePredictionsNew] = useState([]);
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    generateNewSessionToken();
  }, []);
  useImperativeHandle(ref, () => ({
    closeSuggestion,
  }));
  const closeSuggestion = () => {
    setPlacePredictionsNew([]);
  };
  const onChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
  };

  const handleSelectPredictionNew = async (placeId) => {
    try {
      setPlacePredictionsNew([]);
      const response = await axios.get(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': config.googleApi,
            'X-Goog-FieldMask': 'addressComponents',
          },
          params: {
            sessionToken: sessionToken,
          }
        }
      );
      generateNewSessionToken();
      const extracted = extractAddress(response?.data?.addressComponents);
      onSelectAddress(extracted);
      if (inputType === 'city') {
        setInputValue(extracted?.city || '');
      } else {
        setInputValue(extracted?.address || '');
      }
    } catch (error) {
      return null;
    }
  };

  const extractAddress = (addressComponents) => {
    let address = '';
    let city = '';
    let state = '';
    let zipcode = '';

    addressComponents.forEach((component) => {
      if (component.types.includes('street_number')) {
        address += component.longText;  // Street number
      }
      if (component.types.includes('route')) {
        address += ' ' + component.longText;  // Route (street name)
      }
      if (component.types.includes('locality')) {
        city = component.longText;  // City
      }
      if (component.types.includes('administrative_area_level_1')) {
        state = component.shortText;  // State (short code)
      }
      if (component.types.includes('postal_code')) {
        zipcode = component.longText;  // Zipcode
      }
    });
  
    return {
      address: address.trim(),
      city,
      state,
      zipcode,
    };
  };
  
  const searchAddress = (e) => {
    const val = e.target.value;
    getNewAutocompletePredictions(val, sessionToken);
  };

  const getNewAutocompletePredictions = async (input, token) => {
    try {
      // setIsLoading(true);
      const response = await axios.post(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          input: input,
          sessionToken: sessionTokenRef.current,
          includedPrimaryTypes: searchOptions,
          includedRegionCodes: ['us']
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': config.googleApi,
            'X-Goog-FieldMask': 'suggestions.placePrediction.text,suggestions.placePrediction.placeId',
          },
        }
      );
      if (response?.data?.suggestions) {
        setPlacePredictionsNew(response?.data?.suggestions);
      }
    } catch (error) {
      setPlacePredictionsNew([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (inputVal) {
      setInputValue(inputVal);
    }
  }, [inputVal]);

  const debouncedSearch = useRef(debounce(searchAddress, 10)).current;
  const generateNewSessionToken = () => {
    const token = uuidv4();
    setSessionToken(token);
    sessionTokenRef.current = token;
  };
  
  return (
    <div className='location-control'>
      <input
        type="text"
        value={inputValue}
        name={name || ''}
        autocomplete="off"
        aria-autocomplete="none"
        className={className || ''}
        placeholder={placeHolder || 'Search Address'}
        onChange={(e) => {debouncedSearch(e); onChange(e)}}
      />
      { placePredictions.length > 0 && (<>
        <ul className='google-suggest-list'>
          {!isLoading && placePredictions.map((prediction) => {
              const suggestion = prediction?.placePrediction?.text?.text;
              const lcSuggestion = suggestion.toLowerCase();
              const lcInput = inputValue.toLowerCase();
              const matchIndex = lcSuggestion.indexOf(lcInput);

              let text = '';
              if (matchIndex !== -1) {
                const before = suggestion.slice(0, matchIndex);
                const match = suggestion.slice(matchIndex, matchIndex + inputValue.length);
                const after = suggestion.slice(matchIndex + inputValue.length);
                text = `${before}<strong>${match}</strong>${after}`;
              } else {
                text = prediction?.placePrediction?.text?.text;
              }
              return (<li key={prediction.placePrediction.placeId} className='google-suggest-option' onClick={() => handleSelectPredictionNew(prediction.placePrediction.placeId)} >
                <img src={locationIcon} alt='location'/>
                <span
                  dangerouslySetInnerHTML={{ __html: text }}
                >
                </span>
              </li>)
          }
          )}
        </ul>
      </>)}
    </div>
  );
});

export default AutocompleteWithDebounce;
